import { isEmpty } from 'lodash';
import Validator, { createIsNumberRule, ValidatorType } from '../common/form/Validator';
import { ValidationRulesType } from '../production/helpers/productionUtils';
import { OTHER_ATTRIBUTES_KEY } from './attributes.constants';
import { Attribute, AttributeValidationRulesType, AvailableTypeEnum, Field, NumberField } from './attributes.types';
export { removeSpecialCharactersAndCamelizeKey } from './attributes.format';

export const createAttributeValidationRules = (locale) => {
    const attributeValidationRules: AttributeValidationRulesType = {
        [AvailableTypeEnum.text]: () => [Validator.RULES.isRequired],

        [AvailableTypeEnum.number]: (field: Field): ValidatorType[] => [
            createIsNumberRule(locale),
            {
                isValid: (prop, state, isModified, i18n) => {
                    if (state) {
                        const number = field as NumberField;
                        const value: number = parseInt(state[prop]);
                        const min: number = parseInt(String(number.min));
                        const max: number = parseInt(String(number.max));
                        let msg;
                        if (isFinite(min) && isFinite(max)) {
                            if (value < min || value > max) {
                                msg = i18n('validation.number.between', { min: number.min, max: number.max });
                            }
                        } else if (isFinite(min)) {
                            if (value < min) {
                                msg = i18n('validation.number.larger', { min: number.min });
                            }
                        } else if (isFinite(max)) {
                            if (value > max) {
                                msg = i18n('validation.number.smaller', { max: number.max });
                            }
                        }
                        return msg ? msg : true;
                    }
                    return true;
                }
            }
        ],

        [AvailableTypeEnum.termset]: () => [Validator.RULES.isRequired],
        [AvailableTypeEnum.time]: () => [Validator.RULES.isRequired],
        [AvailableTypeEnum.date]: () => [Validator.RULES.isRequired],
        [AvailableTypeEnum.boolean]: () => [],

        [AvailableTypeEnum.decimal]: (field: Field): ValidatorType[] => [
            createIsNumberRule(locale),
            {
                isValid: (prop, state, isModified, i18n) => {
                    if (state) {
                        const number = field as NumberField;
                        const value: number = parseInt(state[prop]);
                        const min: number = parseInt(String(number.min));
                        const max: number = parseInt(String(number.max));
                        let msg;
                        if (isFinite(min) && isFinite(max)) {
                            if (value < min || value > max) {
                                msg = i18n('validation.number.between', { min: number.min, max: number.max });
                            }
                        } else if (isFinite(min)) {
                            if (value < min) {
                                msg = i18n('validation.number.larger', { min: number.min });
                            }
                        } else if (isFinite(max)) {
                            if (value > max) {
                                msg = i18n('validation.number.smaller', { max: number.max });
                            }
                        }
                        return msg ? msg : true;
                    } else {
                        return true;
                    }
                }
            }
        ],

        [AvailableTypeEnum.measurement]: (field: Field): ValidatorType[] => [
            createIsNumberRule(locale),
            {
                isValid: (prop, state, isModified, i18n) => {
                    if (state) {
                        const number = field as NumberField;
                        const value: number = parseInt(state[prop]);
                        const min: number = parseInt(String(number.min));
                        const max: number = parseInt(String(number.max));
                        let msg;
                        if (isFinite(min) && isFinite(max)) {
                            if (value < min || value > max) {
                                msg = i18n('validation.number.between', { min: number.min, max: number.max });
                            }
                        } else if (isFinite(min)) {
                            if (value < min) {
                                msg = i18n('validation.number.larger', { min: number.min });
                            }
                        } else if (isFinite(max)) {
                            if (value > max) {
                                msg = i18n('validation.number.smaller', { max: number.max });
                            }
                        }
                        return msg ? msg : true;
                    } else {
                        return true;
                    }
                }
            }
        ],
        [AvailableTypeEnum.note]: () => [],
        [AvailableTypeEnum.calculationType]: () => [],
        [AvailableTypeEnum.reference]: () => [Validator.RULES.isRequired],
        [AvailableTypeEnum.location]: () => [Validator.RULES.isRequired]
    };

    return attributeValidationRules;
};

export const generateAttributeValidationRules = (fields, locale) => {
    const rules: ValidationRulesType = {};
    const attributeValidationRules = createAttributeValidationRules(locale);

    fields.forEach((field) => {
        const generator = attributeValidationRules[field.fieldType];
        if (generator && typeof generator === 'function') {
            const fieldRules = generator(field);
            if (!isEmpty(fieldRules)) {
                rules[field.name] = fieldRules;
            }
        }
    });

    return rules;
};

export const getAttributesByGroups = (attributes) => {
    const result = {};

    if (Array.isArray(attributes)) {
        attributes.forEach((attr) => {
            const groupKey = attr.group && attr.group.trim() !== '' ? attr.group : OTHER_ATTRIBUTES_KEY;
            result[groupKey] = result?.[groupKey] ? [...result[groupKey], attr] : [attr];
        });
    }

    Object.keys(result).forEach((key) => {
        result[key] = result[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return result;
};

export const getObjectSortedByKeys = (unsorted) => {
    const sortedKeys = Object.keys(unsorted).sort((a, b) => {
        if (a === OTHER_ATTRIBUTES_KEY) return 1;
        if (b === OTHER_ATTRIBUTES_KEY) return -1;
        return a.localeCompare(b);
    });

    const ordered = sortedKeys.reduce((obj, key) => {
        obj[key] = unsorted[key];
        return obj;
    }, {});

    return ordered;
};

export const parseMetadataToFields = (attributes) => {
    if (attributes) {
        const parsedAttributes = attributes.map((attribute) => {
            const { metadata, ...otherFields } = attribute;
            const commonMetadataFields: any = {};
            if (metadata) {
                const metaDataJSON = JSON.parse(metadata);
                commonMetadataFields.required = metaDataJSON.required;
                commonMetadataFields.keyInformation = metaDataJSON.keyInformation;

                if (otherFields.fieldType === AvailableTypeEnum.number) {
                    return { ...otherFields, ...commonMetadataFields, min: metaDataJSON?.min, max: metaDataJSON?.max };
                }
                if (otherFields.fieldType === AvailableTypeEnum.termset) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        termset: metaDataJSON?.termset,
                        newTermset: metaDataJSON?.newTermset ?? [],
                        allowCreate: metaDataJSON?.allowCreate ?? true,
                        isMultiSelect: metaDataJSON?.isMultiSelect
                    };
                }
                if (otherFields.fieldType === AvailableTypeEnum.decimal) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        min: metaDataJSON?.min,
                        max: metaDataJSON?.max,
                        decimalsNumber: metaDataJSON?.decimalsNumber
                    };
                }
                if (otherFields.fieldType === AvailableTypeEnum.measurement) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        min: metaDataJSON?.min,
                        max: metaDataJSON?.max,
                        decimalsNumber: metaDataJSON?.decimalsNumber,
                        measurementType: metaDataJSON?.measurementType,
                        unit: metaDataJSON?.unit
                    };
                }
                if (otherFields.fieldType === AvailableTypeEnum.reference) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        entityToReference: metaDataJSON?.entityToReference,
                        isMultiSelect: metaDataJSON?.isMultiSelect,
                        allowCreate: metaDataJSON?.allowCreate ?? true
                    };
                }
                if (otherFields.fieldType === AvailableTypeEnum.location) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        allowedLocationTypes: metaDataJSON?.allowedLocationTypes,
                        isMultiSelect: metaDataJSON?.isMultiSelect
                    };
                }
                if (otherFields.fieldType === AvailableTypeEnum.note) {
                    return {
                        ...otherFields,
                        ...commonMetadataFields,
                        hasAttachments: metaDataJSON?.attachments?.macroEnabled ?? false,
                        autoOpenSlashMacro: metaDataJSON?.attachments?.autoLaunch ?? false
                    };
                }
            }
            return { ...otherFields, ...commonMetadataFields };
        });
        return parsedAttributes;
    }
    return attributes;
};

export const parseFieldsToMetadata = (fieldDefinition) => {
    const {
        min,
        max,
        typeName,
        decimalsNumber,
        required,
        keyInformation,
        termset,
        newTermset,
        allowCreate,
        selectType,
        isMultiSelect,
        measurementType,
        unit,
        entityToReference,
        ...otherFields
    } = fieldDefinition;
    let metadata = '';
    if (fieldDefinition.fieldType === AvailableTypeEnum.number) {
        metadata = JSON.stringify({ min, max });
    }
    if (fieldDefinition.fieldType === AvailableTypeEnum.termset) {
        metadata = JSON.stringify({ required, keyInformation, termset, newTermset, allowCreate, isMultiSelect });
    }
    if (fieldDefinition.fieldType === AvailableTypeEnum.decimal) {
        metadata = JSON.stringify({ min, max, decimalsNumber });
    }
    if (fieldDefinition.fieldType === AvailableTypeEnum.measurement) {
        metadata = JSON.stringify({ min, max, decimalsNumber, required, keyInformation, measurementType, unit });
    }
    if (fieldDefinition.fieldType === AvailableTypeEnum.reference) {
        metadata = JSON.stringify({
            required,
            keyInformation,
            entityToReference,
            isMultiSelect,
            allowCreate: allowCreate !== undefined ? allowCreate : true
        });
    }
    if (fieldDefinition.fieldType === AvailableTypeEnum.location) {
        const { allowedLocationTypes, ...locationOtherFields } = fieldDefinition;
        metadata = JSON.stringify({
            required,
            isMultiSelect,
            allowedLocationTypes
        });
        return {
            ...locationOtherFields,
            ...otherFields,
            metadata,
            isRequired: Boolean(required)
        };
    }
    return {
        ...otherFields,
        metadata,
        isRequired: Boolean(required),
        isKey: Boolean(keyInformation)
    };
};

export const getSupportedItems = (items) => {
    return items.filter((item) => Object.values(AvailableTypeEnum).includes(item.fieldType));
};

export const getNonSavedFieldDefinitions = (fieldDef, associatedFieldDefs) => {
    const existingIds = associatedFieldDefs?.map((field) => field.id);
    const nonExistingFields = fieldDef.filter((field) => !existingIds?.includes(field.id));
    return nonExistingFields;
};

export function processAttributes(attributes: Attribute[], attr: any) {
    return attributes
        .map((attribute) => {
            const { value, metadata, ...otherAttributeFields } = attribute;
            if (attribute.fieldType === AvailableTypeEnum.measurement) {
                return {
                    ...otherAttributeFields,
                    value: JSON.stringify({
                        value: attr[attribute.name],
                        unit: attr[attribute.name + 'unit']
                    })
                };
            }
            if (attr[attribute?.name] || attribute.fieldType === AvailableTypeEnum.boolean) {
                return { ...otherAttributeFields, value: attr[attribute.name] };
            }
        })
        .filter(Boolean);
}
